import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { IEvent } from '../types';

interface IModalContent {
	event: IEvent;
	isSameDayEvent: boolean;
}
interface IModalContext {
	isOpen: boolean;
	content: IModalContent | null;
	openModal: (content: IModalContent) => void;
	closeModal: () => void;
}

const defaultModalContextValue: IModalContext = {
	isOpen: false,
	content: null,
	openModal: () => {},
	closeModal: () => {},
};

const ModalContext = createContext<IModalContext>(defaultModalContextValue);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [content, setContent] = useState<IModalContent | null>(null);

	const openModal = useCallback((content: IModalContent) => {
		setContent(content);
		setIsOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsOpen(false);
		setContent(null);
	}, []);

	const contextValue = useMemo(
		() => ({
			isOpen,
			content,
			openModal,
			closeModal,
		}),
		[isOpen, content, openModal, closeModal]
	);

	return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};

export const useModal = () => useContext(ModalContext);
